import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { shopsApi } from '../../lib/api'
import { Menu, ChevronRight, LogOut, RefreshCw } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import RoleBadge from '../shared/RoleBadge'
import { tx } from '../../lib/i18n'
import LangSwitch from '../shared/LangSwitch'

const routeLabels: Record<string, string> = {
  dashboard: tx('Dashboard'),
  shops: tx('Shops'),
  subscriptions: tx('Subscriptions'),
  plans: tx('Plans'),
  billing: tx('Billing'),
  'money-logs': tx('Money Logs'),
  'ai-usage': tx('AI Usage'),
  'customer-analytics': tx('Customer Analytics'),
  map: tx('Shops map'),
  'feature-flags': tx('Feature Flags'),
  'menu-control': tx('Menu Control'),
  notifications: tx('Notifications'),
  support: tx('Support'),
  'admin-users': tx('Admin Users'),
  'audit-logs': tx('Audit Logs'),
  'system-settings': tx('System Settings'),
  integrations: tx('Integrations'),
  'system-health': tx('System Health'),
  'shop-payments': tx('Shop Payments'),
  'subscription-promotions': tx('Plan Promotions'),
  referrers: tx('Sales & Affiliates'),
  consents: tx('Consent Documents'),
  announcements: tx('Announcements'),
  'usage-analytics': tx('Menu & Button Usage'),
  'business-insights': tx('Business Stats & Targets'),
  'finance-reports': tx('Accounting Reports'),
  'master-data': tx('Master Data'),
  'business-types': tx('Business Types'),
  'payment-methods': tx('Payment Methods'),
  account: tx('My Account'),
}

interface TopbarProps {
  onMenuClick: () => void
}

const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const { currentAdmin, logout } = useAuth()
  const location = useLocation()

  const pathParts = location.pathname.split('/').filter(Boolean)
  // /shops/:id — show the shop's name instead of its UUID (shares the detail page's cached query).
  const shopId = pathParts[0] === 'shops' && pathParts[1] && pathParts[1] !== 'map' ? pathParts[1] : undefined
  const { data: crumbShop } = useQuery({
    queryKey: ['shop', shopId],
    queryFn: () => shopsApi.get(shopId!).then((r) => r.data.data),
    enabled: !!shopId,
  })
  const isId = (part: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(part) || /^c[a-z0-9]{20,}$/.test(part) || /^\d{4}-\d{2}-\d{2}_/.test(part)
  const crumbs: { label: string; href: string }[] = []
  let accumulated = ''
  for (const part of pathParts) {
    accumulated += `/${part}`
    const label =
      routeLabels[part] ??
      (part === shopId ? crumbShop?.name ?? '…' : isId(part) ? tx('Details') : decodeURIComponent(part))
    crumbs.push({ label, href: accumulated })
  }

  const pageTitle = crumbs.length > 0 ? crumbs[crumbs.length - 1].label : 'Dashboard'

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
      <div className="flex items-center justify-between px-4 md:px-6 h-14">
        {/* Left: hamburger + breadcrumb */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onMenuClick}
            className="md:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <nav className="flex items-center gap-1 text-sm min-w-0">
            {crumbs.map((crumb, i) => (
              <React.Fragment key={crumb.href}>
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />}
                {i < crumbs.length - 1 ? (
                  <Link
                    to={crumb.href}
                    className="text-gray-400 hover:text-gray-600 font-medium truncate"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900 font-semibold truncate">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
            {crumbs.length === 0 && (
              <span className="text-gray-900 font-semibold">{pageTitle}</span>
            )}
          </nav>
        </div>

        {/* Right: admin info + logout */}
        {currentAdmin && (
          <div className="flex items-center gap-3 flex-shrink-0">
            <LangSwitch />
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {currentAdmin.firstName} {currentAdmin.lastName}
              </span>
              <RoleBadge role={currentAdmin.role} label={currentAdmin.roleLabel} size="sm" />
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{tx('Logout')}</span>
            </button>
          </div>
        )}
      </div>
    </header>
  )
}

export default Topbar
